import type { ShowcaseVideo } from '@/types/home'

const FALLBACK_THUMB =
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&h=500&fit=crop&q=80'

export function getYoutubeThumbnail(videoId: string, quality: 'hq' | 'max' | 'mq' = 'hq') {
  const file =
    quality === 'max' ? 'maxresdefault' : quality === 'mq' ? 'mqdefault' : 'hqdefault'
  return `https://img.youtube.com/vi/${videoId}/${file}.jpg`
}

export function getShowcaseThumbnail(video: ShowcaseVideo) {
  if (video.thumbnail) return video.thumbnail
  if (video.platform === 'youtube' && video.videoId) {
    return getYoutubeThumbnail(video.videoId, 'hq')
  }
  return FALLBACK_THUMB
}

export function getShowcaseThumbnailFallbacks(video: ShowcaseVideo): string[] {
  const urls: string[] = []
  if (video.thumbnail) urls.push(video.thumbnail)
  if (video.platform === 'youtube' && video.videoId) {
    urls.push(getYoutubeThumbnail(video.videoId, 'hq'))
    urls.push(getYoutubeThumbnail(video.videoId, 'mq'))
  }
  urls.push(FALLBACK_THUMB)
  return [...new Set(urls)]
}

export function getYoutubeEmbedUrl(videoId: string, autoplay = true) {
  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    rel: '0',
    modestbranding: '1',
  })
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`
}

export function getYoutubePreviewEmbedUrl(videoId: string) {
  const params = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    controls: '0',
    loop: '1',
    playlist: videoId,
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
    disablekb: '1',
    fs: '0',
  })
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`
}

export function extractYoutubeVideoId(link: string | null | undefined): string | null {
  const raw = (link ?? '').trim()
  if (!raw) return null
  if (!raw.includes('/') && !raw.includes('.')) return raw

  try {
    if (raw.includes('youtube.com/watch')) {
      return new URL(raw).searchParams.get('v')
    }
    if (raw.includes('youtu.be/')) {
      return raw.split('youtu.be/')[1]?.split('?')[0] ?? null
    }
    if (raw.includes('youtube.com/embed/')) {
      return raw.split('embed/')[1]?.split('?')[0] ?? null
    }
  } catch {
    return null
  }

  const match = raw.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/)
  return match?.[1] ?? null
}

export function getShowcaseOpenUrl(video: ShowcaseVideo) {
  if (video.platform === 'instagram' && video.url) return video.url
  if (video.platform === 'youtube' && video.videoId) {
    return `https://www.youtube.com/watch?v=${video.videoId}`
  }
  return video.url
}

export function formatMediaCount(value: number) {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1).replace(/\.0$/, '')}K`
  return String(value)
}

export function formatUploadDate(date?: string) {
  if (!date) return undefined
  return new Date(date).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
